import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import type {
  DailyTaskStatus,
  DailyTasksState,
  DailyTaskTemplateFirestore,
} from './dailyTaskTemplateTypes';

const COLLECTION_NAME = 'daily_task_templates';

/** Firestore rejects `undefined` field values. Strip them for create. */
const stripUndefined = <T extends Record<string, unknown>>(data: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined),
  ) as Partial<T>;
};

/** For updates, turn `undefined` fields into deleteField() so they're removed. */
const withDeletedFields = <T extends Record<string, unknown>>(data: T): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(data).map(([k, v]) => [k, v === undefined ? deleteField() : v]),
  );
};

export class DailyTaskTemplateService {
  private static instance: DailyTaskTemplateService;

  private constructor() {}

  public static getInstance(): DailyTaskTemplateService {
    if (!DailyTaskTemplateService.instance) {
      DailyTaskTemplateService.instance = new DailyTaskTemplateService();
    }
    return DailyTaskTemplateService.instance;
  }

  public async getAll(): Promise<DailyTaskTemplateFirestore[]> {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    })) as DailyTaskTemplateFirestore[];
  }

  public async create(
    template: Omit<DailyTaskTemplateFirestore, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<string> {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...stripUndefined(template),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  }

  public async update(template: DailyTaskTemplateFirestore): Promise<void> {
    if (!template.id) throw new Error('Template id is required for update');
    const ref = doc(db, COLLECTION_NAME, template.id);
    const { id, ...dataWithoutId } = template;
    void id;
    await updateDoc(ref, { ...withDeletedFields(dataWithoutId), updatedAt: serverTimestamp() });
  }

  public async remove(templateId: string): Promise<void> {
    const ref = doc(db, COLLECTION_NAME, templateId);
    await deleteDoc(ref);
  }

  public async importTemplates(templates: DailyTaskTemplateFirestore[]): Promise<void> {
    const existing = await this.getAll();
    const existingIds = new Set(existing.map((t) => t.id));

    const batch = writeBatch(db);
    for (const template of templates) {
      const { id, createdAt, updatedAt, ...data } = template;
      void createdAt;
      void updatedAt;

      const ref = id ? doc(db, COLLECTION_NAME, id) : doc(collection(db, COLLECTION_NAME));
      const isNew = !id || !existingIds.has(id);

      if (isNew) {
        batch.set(ref, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      } else {
        batch.set(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
      }
    }
    await batch.commit();
  }

  public async resetUserDailyTasks(userId: string): Promise<void> {
    const userRef = doc(db, 'user_info', userId);
    await updateDoc(userRef, { dailyTasks: deleteField() });
  }

  /** Read a user's generated daily tasks from user_info/{uid}.dailyTasks. */
  public async getUserDailyTasks(userId: string): Promise<DailyTasksState | null> {
    const snap = await getDoc(doc(db, 'user_info', userId));
    const data = snap.data();
    return (data?.dailyTasks as DailyTasksState | undefined) ?? null;
  }

  /** Update a single generated task's status and/or completed steps. */
  public async updateUserDailyTask(
    userId: string,
    taskId: string,
    patch: { status?: DailyTaskStatus; completedSteps?: number },
  ): Promise<void> {
    const update: Record<string, unknown> = {};
    if (patch.status !== undefined) {
      update[`dailyTasks.tasks.${taskId}.status`] = patch.status;
    }
    if (patch.completedSteps !== undefined) {
      update[`dailyTasks.tasks.${taskId}.completedSteps`] = patch.completedSteps;
    }
    if (Object.keys(update).length === 0) return;

    const userRef = doc(db, 'user_info', userId);
    await updateDoc(userRef, update);
  }
}

export const dailyTaskTemplateService = DailyTaskTemplateService.getInstance();
